import { useState, useEffect } from 'react';
import { loadingPhrases } from '../constants';

// Bouncing balls loading component with random heights and pauses
const BouncingBalls = () => {
  const [waveKey, setWaveKey] = useState(0);
  const [heights, setHeights] = useState(() => [0, 1, 2, 3].map(() => 10 + Math.random() * 8));

  useEffect(() => {
    const triggerNewWave = () => {
      // Generate new random heights for each ball (10-18px range)
      setHeights([0, 1, 2, 3].map(() => 10 + Math.random() * 8));
      setWaveKey((k) => k + 1);

      // Schedule next wave with random pause (1.5-2.5s total cycle)
      const nextPause = 1500 + Math.random() * 1000;
      return setTimeout(triggerNewWave, nextPause);
    };

    const timeoutId = triggerNewWave();
    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <div className='flex items-end gap-1.5 h-8'>
      {[0, 1, 2, 3].map((i) => (
        <div
          key={`${waveKey}-${i}`}
          className='w-2.5 h-2.5 bg-emerald-500 rounded-full'
          style={{
            animation: `ballBounce 0.5s ease-out`,
            animationDelay: `${i * 0.08}s`,
            '--bounce-height': `-${heights[i]}px`,
          }}
        />
      ))}
      <style>{`
        @keyframes ballBounce {
          0% { transform: translateY(0) scale(1); }
          30% { transform: translateY(var(--bounce-height)) scale(0.95, 1.05); }
          50% { transform: translateY(0) scale(1.1, 0.9); }
          70% { transform: translateY(calc(var(--bounce-height) * 0.3)) scale(1); }
          85% { transform: translateY(0) scale(1.03, 0.97); }
          100% { transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
};

// Animated loading text that cycles through phrases
const AnimatedLoadingText = () => {
  const [phraseIndex, setPhraseIndex] = useState(() =>
    Math.floor(Math.random() * loadingPhrases.length)
  );
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    let timeoutId = null;
    const interval = setInterval(() => {
      setIsVisible(false);
      timeoutId = setTimeout(() => {
        setPhraseIndex((prev) => (prev + 1) % loadingPhrases.length);
        setIsVisible(true);
      }, 300); // fade out duration
    }, 5000); // rotate every 5 seconds

    return () => {
      clearInterval(interval);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  return (
    <p
      className={`text-xl text-gray-600 font-light transition-opacity duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}>
      {loadingPhrases[phraseIndex]}
    </p>
  );
};

/**
 * Displays a full-screen loading overlay with bouncing ball animation and rotating text phrases.
 * @param {Object} props
 * @param {string} props.text - Loading message to display; when set to "Generating", cycles through animated phrases.
 */
export const LoadingScreen = ({ text }) => (
  <div className='fixed inset-0 bg-gray-50 flex flex-col items-center justify-center z-50'>
    <div className='flex flex-col items-center gap-6'>
      <BouncingBalls />
      {text === 'Generating' ? (
        <AnimatedLoadingText />
      ) : (
        <p className='text-xl text-gray-600 font-light'>{text}</p>
      )}
    </div>
  </div>
);
